import React from 'react'
import { useNavigate } from 'react-router-dom'
import { TrendingUp, TrendingDown, DollarSign, Clock, Star, Target, Award, Calendar, BarChart3, PieChart, Download, Filter, ArrowLeft } from 'lucide-react'
import { toast } from '../lib/toast'

interface PerformanceData {
  period: string
  earnings: number
  jobs: number
  hours: number
  rating: number
  completionRate: number
}

interface ServicePerformance {
  service: string
  jobs: number
  revenue: number
  averageRating: number
  growth: number
}

interface MonthlyData {
  month: string
  earnings: number
  jobs: number
  hours: number
  rating: number
}

export default function ProviderPerformance() {
  const navigate = useNavigate()
  
  const handleBackToDashboard = () => {
    navigate('/dashboard')
  }

  const [selectedPeriod, setSelectedPeriod] = React.useState<string>('all')
  const [selectedMetric, setSelectedMetric] = React.useState<string>('all')

  // Mock performance data
  const monthlyData: MonthlyData[] = [
    { month: 'Jan', earnings: 2800, jobs: 18, hours: 72, rating: 4.8 },
    { month: 'Feb', earnings: 3200, jobs: 22, hours: 88, rating: 4.9 },
    { month: 'Mar', earnings: 3500, jobs: 24, hours: 96, rating: 4.7 },
    { month: 'Apr', earnings: 3100, jobs: 20, hours: 80, rating: 4.8 },
    { month: 'May', earnings: 3800, jobs: 26, hours: 104, rating: 4.9 },
    { month: 'Jun', earnings: 4200, jobs: 28, hours: 112, rating: 5.0 }
  ]

  const servicePerformance: ServicePerformance[] = [
    {
      service: 'Standard House Cleaning',
      jobs: 45,
      revenue: 6750,
      averageRating: 4.8,
      growth: 12.5
    },
    {
      service: 'Deep Kitchen Cleaning',
      jobs: 28,
      revenue: 5600,
      averageRating: 4.9,
      growth: 8.3
    },
    {
      service: 'Office Cleaning',
      jobs: 15,
      revenue: 4500,
      averageRating: 5.0,
      growth: 15.2
    },
    {
      service: 'Carpet Shampooing',
      jobs: 12,
      revenue: 1440,
      averageRating: 4.6,
      growth: -5.1
    }
  ]

  const currentPeriod = {
    earnings: 4200,
    jobs: 28,
    hours: 112,
    rating: 5.0,
    completionRate: 98.5,
    averageJobValue: 150,
    repeatClients: 32,
    newClients: 8
  }

  const previousPeriod = {
    earnings: 3800,
    jobs: 26,
    hours: 104,
    rating: 4.9,
    completionRate: 97.2,
    averageJobValue: 146,
    repeatClients: 30,
    newClients: 6
  }

  const calculateGrowth = (current: number, previous: number) => {
    return ((current - previous) / previous * 100).toFixed(1)
  }

  // Filter data based on selected period and metric
  const getFilteredData = () => {
    let periodMultiplier = 1
    let metricFilter = (data: any) => data
    
    // Apply period filter
    switch (selectedPeriod) {
      case 'week':
        periodMultiplier = 0.25
        break
      case 'month':
        periodMultiplier = 1
        break
      case 'quarter':
        periodMultiplier = 3
        break
      case 'year':
        periodMultiplier = 12
        break
      case 'all':
        periodMultiplier = 12 // Show annual data for "all"
        break
      default:
        periodMultiplier = 1
    }
    
    // Apply metric filter
    const baseCurrent = currentPeriod
    const basePrevious = previousPeriod
    
    return {
      current: {
        earnings: selectedMetric === 'all' || selectedMetric === 'earnings' ? Math.round(baseCurrent.earnings * periodMultiplier) : 0,
        jobs: selectedMetric === 'all' || selectedMetric === 'jobs' ? Math.round(baseCurrent.jobs * periodMultiplier) : 0,
        hours: selectedMetric === 'all' || selectedMetric === 'hours' ? Math.round(baseCurrent.hours * periodMultiplier) : 0,
        rating: selectedMetric === 'all' || selectedMetric === 'rating' ? baseCurrent.rating : 0,
        completionRate: selectedMetric === 'all' ? baseCurrent.completionRate : 0,
        averageJobValue: selectedMetric === 'all' ? baseCurrent.averageJobValue : 0,
        repeatClients: selectedMetric === 'all' ? Math.round(baseCurrent.repeatClients * periodMultiplier) : 0,
        newClients: selectedMetric === 'all' ? Math.round(baseCurrent.newClients * periodMultiplier) : 0
      },
      previous: {
        earnings: selectedMetric === 'all' || selectedMetric === 'earnings' ? Math.round(basePrevious.earnings * periodMultiplier) : 0,
        jobs: selectedMetric === 'all' || selectedMetric === 'jobs' ? Math.round(basePrevious.jobs * periodMultiplier) : 0,
        hours: selectedMetric === 'all' || selectedMetric === 'hours' ? Math.round(basePrevious.hours * periodMultiplier) : 0,
        rating: selectedMetric === 'all' || selectedMetric === 'rating' ? basePrevious.rating : 0,
        completionRate: selectedMetric === 'all' ? basePrevious.completionRate : 0,
        averageJobValue: selectedMetric === 'all' ? basePrevious.averageJobValue : 0,
        repeatClients: selectedMetric === 'all' ? Math.round(basePrevious.repeatClients * periodMultiplier) : 0,
        newClients: selectedMetric === 'all' ? Math.round(basePrevious.newClients * periodMultiplier) : 0
      }
    }
  }
  
  const filteredData = getFilteredData()
  const displayCurrent = filteredData.current
  const displayPrevious = filteredData.previous

  const getGrowthIcon = (growth: string) => {
    const growthValue = parseFloat(growth)
    if (growthValue > 0) return <TrendingUp className="w-4 h-4 text-green-500" />
    if (growthValue < 0) return <TrendingDown className="w-4 h-4 text-red-500" />
    return <div className="w-4 h-4" />
  }

  const getGrowthColor = (growth: string) => {
    const growthValue = parseFloat(growth)
    if (growthValue > 0) return 'text-green-600'
    if (growthValue < 0) return 'text-red-600'
    return 'text-gray-600'
  }

  const handleExportReport = () => {
    const generateHTMLReport = () => {
      const currentDate = new Date().toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
      
      const html = `
<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Performance Report - SmartClean</title>
    <style>
        * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
        }
        
        body {
            font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
            line-height: 1.6;
            color: #333;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            min-height: 100vh;
            padding: 20px;
        }
        
        .container {
            max-width: 1200px;
            margin: 0 auto;
            background: white;
            border-radius: 20px;
            box-shadow: 0 20px 40px rgba(0,0,0,0.1);
            overflow: hidden;
        }
        
        .header {
            background: linear-gradient(135deg, #10b981 0%, #059669 100%);
            color: white;
            padding: 25px 40px;
            text-align: center;
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 15px;
        }
        
        .header h1 {
            font-size: 2.5em;
            margin-bottom: 5px;
            font-weight: 900;
            color: #fbbf24;
            text-shadow: 2px 2px 4px rgba(0,0,0,0.3);
            letter-spacing: -1px;
        }
        
        .header h2 {
            font-size: 1.8em;
            margin-bottom: 5px;
            font-weight: 500;
            opacity: 0.9;
        }
        
        .header h3 {
            font-size: 1.2em;
            margin-bottom: 5px;
            font-weight: 400;
            opacity: 0.7;
            color: #fbbf24;
        }
        
        .header p {
            font-size: 1em;
            opacity: 0.8;
        }
        
        .logo-container {
            display: flex;
            align-items: center;
            gap: 15px;
        }
        
        .logo {
            width: 60px;
            height: 60px;
            background: white;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 28px;
            color: #10b981;
            font-weight: 900;
            box-shadow: 0 4px 15px rgba(0,0,0,0.2);
        }
        
        .content {
            padding: 40px;
        }
        
        .section {
            margin-bottom: 40px;
        }
        
        .section h2 {
            color: #10b981;
            font-size: 1.8em;
            margin-bottom: 20px;
            border-bottom: 3px solid #10b981;
            padding-bottom: 10px;
        }
        
        .kpi-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
            gap: 20px;
            margin-bottom: 30px;
        }
        
        .kpi-card {
            background: linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%);
            padding: 25px;
            border-radius: 15px;
            text-align: center;
            border: 2px solid #10b981;
            transition: transform 0.3s ease;
        }
        
        .kpi-card:hover {
            transform: translateY(-5px);
        }
        
        .kpi-value {
            font-size: 2.5em;
            font-weight: 700;
            color: #10b981;
            margin-bottom: 5px;
        }
        
        .kpi-label {
            color: #6b7280;
            font-weight: 600;
        }
        
        .kpi-change {
            font-size: 0.9em;
            margin-top: 5px;
            color: #059669;
        }
        
        .service-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
            gap: 20px;
        }
        
        .service-card {
            background: white;
            border: 2px solid #e5e7eb;
            border-radius: 12px;
            padding: 20px;
            box-shadow: 0 4px 6px rgba(0,0,0,0.1);
        }
        
        .service-card h3 {
            color: #1f2937;
            margin-bottom: 15px;
            font-size: 1.3em;
        }
        
        .service-stats {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 10px;
        }
        
        .stat-item {
            padding: 10px;
            background: #f9fafb;
            border-radius: 8px;
            text-align: center;
        }
        
        .stat-value {
            font-weight: 700;
            color: #10b981;
        }
        
        .stat-label {
            font-size: 0.85em;
            color: #6b7280;
        }
        
        .trend-section {
            background: #f9fafb;
            padding: 30px;
            border-radius: 15px;
            margin-top: 30px;
        }
        
        .trend-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
            gap: 20px;
        }
        
        .trend-item {
            text-align: center;
        }
        
        .trend-bar {
            height: 8px;
            background: #e5e7eb;
            border-radius: 4px;
            margin: 10px 0;
            position: relative;
            overflow: hidden;
        }
        
        .trend-fill {
            height: 100%;
            border-radius: 4px;
            transition: width 1s ease;
        }
        
        .footer {
            text-align: center;
            padding: 30px;
            background: #f3f4f6;
            color: #6b7280;
            font-size: 0.9em;
        }
        
        .logo {
            font-size: 1.5em;
            font-weight: 700;
            color: #10b981;
            margin-bottom: 5px;
        }
        
        .print-button {
            position: fixed;
            top: 20px;
            right: 20px;
            background: linear-gradient(135deg, #10b981 0%, #059669 100%);
            color: white;
            border: none;
            padding: 12px 24px;
            border-radius: 8px;
            font-size: 16px;
            font-weight: 600;
            cursor: pointer;
            box-shadow: 0 4px 15px rgba(0,0,0,0.2);
            transition: all 0.3s ease;
            z-index: 1000;
        }
        
        .print-button:hover {
            transform: translateY(-2px);
            box-shadow: 0 6px 20px rgba(0,0,0,0.3);
        }
        
        @media print {
            body { background: white; }
            .container { box-shadow: none; }
            .kpi-card:hover { transform: none; }
            .print-button { display: none; }
        }
    </style>
</head>
<body>
    <button class="print-button" onclick="window.print()">
        🖨️ Print Report
    </button>
    
    <div class="container">
        <div class="header">
            <div class="logo-container">
                <div class="logo">🛡️</div>
                <h1>SmartClean</h1>
            </div>
            <h2>Performance Report</h2>
            <h3>${selectedPeriod === 'month' ? 'Monthly Performance' : selectedPeriod === 'quarter' ? 'Quarterly Performance' : selectedPeriod === 'year' ? 'Annual Performance' : 'Performance Overview'}</h3>
        </div>
        
        <div class="content">
            <div class="section">
                <h2>📊 Key Performance Indicators</h2>
                <div class="kpi-grid">
                    <div class="kpi-card">
                        <div class="kpi-value">$${displayCurrent.earnings.toLocaleString()}</div>
                        <div class="kpi-label">Total Revenue</div>
                        <div class="kpi-change">+${displayCurrent.earnings - displayPrevious.earnings} from last period</div>
                    </div>
                    <div class="kpi-card">
                        <div class="kpi-value">${displayCurrent.jobs}</div>
                        <div class="kpi-label">Completed Jobs</div>
                        <div class="kpi-change">+${displayCurrent.jobs - displayPrevious.jobs} from last period</div>
                    </div>
                    <div class="kpi-card">
                        <div class="kpi-value">${displayCurrent.hours}h</div>
                        <div class="kpi-label">Total Hours</div>
                        <div class="kpi-change">+${displayCurrent.hours - displayPrevious.hours}h from last period</div>
                    </div>
                    <div class="kpi-card">
                        <div class="kpi-value">${displayCurrent.completionRate}%</div>
                        <div class="kpi-label">Completion Rate</div>
                        <div class="kpi-change">+${(displayCurrent.completionRate - displayPrevious.completionRate).toFixed(1)}% from last period</div>
                    </div>
                </div>
            </div>
            
            <div class="section">
                <h2>🔧 Service Performance</h2>
                <div class="service-grid">
                    ${servicePerformance.map(service => `
                        <div class="service-card">
                            <h3>${service.service}</h3>
                            <div class="service-stats">
                                <div class="stat-item">
                                    <div class="stat-value">${service.jobs}</div>
                                    <div class="stat-label">Jobs</div>
                                </div>
                                <div class="stat-item">
                                    <div class="stat-value">$${(service.revenue / 1000).toFixed(1)}k</div>
                                    <div class="stat-label">Revenue</div>
                                </div>
                                <div class="stat-item">
                                    <div class="stat-value">⭐ ${service.averageRating.toFixed(1)}</div>
                                    <div class="stat-label">Rating</div>
                                </div>
                                <div class="stat-item">
                                    <div class="stat-value">${service.growth > 0 ? '+' : ''}${service.growth}%</div>
                                    <div class="stat-label">Growth</div>
                                </div>
                            </div>
                        </div>
                    `).join('')}
                </div>
            </div>
            
            <div class="section">
                <h2>📈 6-Month Performance Trend</h2>
                <div class="trend-section">
                    <div class="trend-grid">
                        <div class="trend-item">
                            <h4>Revenue Trend</h4>
                            ${monthlyData.map(data => `
                                <div style="font-size: 0.8em; margin: 5px 0;">${data.month}</div>
                                <div class="trend-bar">
                                    <div class="trend-fill" style="width: ${(data.earnings / Math.max(...monthlyData.map(d => d.earnings))) * 100}%; background: #10b981;"></div>
                                </div>
                                <div style="font-weight: bold; color: #10b981;">$${(data.earnings / 1000).toFixed(1)}k</div>
                            `).join('')}
                        </div>
                        <div class="trend-item">
                            <h4>Jobs Trend</h4>
                            ${monthlyData.map(data => `
                                <div style="font-size: 0.8em; margin: 5px 0;">${data.month}</div>
                                <div class="trend-bar">
                                    <div class="trend-fill" style="width: ${(data.jobs / Math.max(...monthlyData.map(d => d.jobs))) * 100}%; background: #3b82f6;"></div>
                                </div>
                                <div style="font-weight: bold; color: #3b82f6;">${data.jobs}</div>
                            `).join('')}
                        </div>
                        <div class="trend-item">
                            <h4>Hours Trend</h4>
                            ${monthlyData.map(data => `
                                <div style="font-size: 0.8em; margin: 5px 0;">${data.month}</div>
                                <div class="trend-bar">
                                    <div class="trend-fill" style="width: ${(data.hours / Math.max(...monthlyData.map(d => d.hours))) * 100}%; background: #8b5cf6;"></div>
                                </div>
                                <div style="font-weight: bold; color: #8b5cf6;">${data.hours}h</div>
                            `).join('')}
                        </div>
                        <div class="trend-item">
                            <h4>Rating Trend</h4>
                            ${monthlyData.map(data => `
                                <div style="font-size: 0.8em; margin: 5px 0;">${data.month}</div>
                                <div class="trend-bar">
                                    <div class="trend-fill" style="width: ${(data.rating / 5) * 100}%; background: #f59e0b;"></div>
                                </div>
                                <div style="font-weight: bold; color: #f59e0b;">⭐ ${data.rating.toFixed(1)}</div>
                            `).join('')}
                        </div>
                    </div>
                </div>
            </div>
        </div>
        
        <div class="footer">
            <p>© 2026 SmartClean. Professional Cleaning Performance Report</p>
            <p>This report was generated automatically from your performance data.</p>
        </div>
    </div>
</body>
</html>`;
      
      return html;
    };
    
    const htmlContent = generateHTMLReport();
    const blob = new Blob([htmlContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `performance-report-${new Date().toISOString().split('T')[0]}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    toast.success('Performance report exported successfully!');
  };

  const renderMiniChart = (data: number[], color: string) => {
    const max = Math.max(...data)
    const min = Math.min(...data)
    const range = max - min || 1
    
    return (
      <div className="flex items-end space-x-1 h-8">
        {data.map((value, index) => {
          const height = ((value - min) / range) * 100
          return (
            <div
              key={index}
              className={`w-2 ${color} rounded-t`}
              style={{ height: `${height}%` }}
            />
          )
        })}
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-dark-bg">
      {/* Header */}
      <header className="bg-white dark:bg-dark-card shadow-sm border-b border-gray-200 dark:border-dark-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-4">
              <BarChart3 className="w-6 h-6 text-purple-600" />
              <div>
                <h1 className="text-xl font-bold text-gray-900 dark:text-dark-text-primary">Performance Report</h1>
                <p className="text-sm text-gray-500 dark:text-dark-text-secondary">Track your business performance and growth</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              <button
                onClick={handleExportReport}
                className="flex items-center space-x-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
              >
                <Download className="w-4 h-4" />
                <span>Export Report</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Period Selector */}
        <div className="bg-white dark:bg-dark-card rounded-xl shadow-sm border border-gray-200 dark:border-dark-border p-2 md:p-3 mb-3">
          <div className="flex flex-col md:flex-row justify-between items-center gap-3">
            <div>
              <h2 className="text-sm md:text-base font-semibold text-gray-900 dark:text-dark-text-primary">Filter Report</h2>
              <p className="text-xs text-gray-500 dark:text-gray-400 hidden md:block">Select period and metrics to customize view</p>
            </div>
            
            <div className="flex items-center space-x-2">
              <select
                value={selectedPeriod}
                onChange={(e) => setSelectedPeriod(e.target.value)}
                className="px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 dark:focus:ring-purple-400 dark:focus:border-purple-400 bg-white dark:bg-dark-card text-gray-900 dark:text-dark-text-primary"
              >
                <option value="all">All Time</option>
                <option value="week">Week</option>
                <option value="month">Month</option>
                <option value="quarter">Quarter</option>
                <option value="year">Year</option>
              </select>
              
              <select
                value={selectedMetric}
                onChange={(e) => setSelectedMetric(e.target.value)}
                className="px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 dark:focus:ring-purple-400 dark:focus:border-purple-400 bg-white dark:bg-dark-card text-gray-900 dark:text-dark-text-primary"
              >
                <option value="all">All Metrics</option>
                <option value="earnings">Earnings</option>
                <option value="jobs">Jobs</option>
                <option value="rating">Rating</option>
                <option value="hours">Hours</option>
              </select>
            </div>
          </div>
        </div>

        {/* Key Performance Indicators */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-1 md:gap-6 mb-8">
          <div className="bg-white dark:bg-dark-card p-6 rounded-xl shadow-sm border border-gray-200 dark:border-dark-border">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2">
                <DollarSign className="w-5 h-5 text-green-600" />
                <span className="text-sm font-medium text-gray-600 dark:text-dark-text-secondary">Revenue</span>
              </div>
              <div className="flex items-center space-x-1">
                {getGrowthIcon(calculateGrowth(displayCurrent.earnings, displayPrevious.earnings))}
                <span className={`text-sm font-medium ${getGrowthColor(calculateGrowth(displayCurrent.earnings, displayPrevious.earnings))}`}>
                  {calculateGrowth(displayCurrent.earnings, displayPrevious.earnings)}%
                </span>
              </div>
            </div>
            <div className="space-y-2">
              <p className="text-2xl font-bold text-gray-900 dark:text-dark-text-primary">${displayCurrent.earnings.toLocaleString()}</p>
              <p className="text-xs text-gray-500 dark:text-dark-text-muted">Previous: ${displayPrevious.earnings.toLocaleString()}</p>
              {renderMiniChart(monthlyData.map(d => d.earnings), 'bg-green-200 dark:bg-green-700')}
            </div>
          </div>

          <div className="bg-white dark:bg-dark-card p-6 rounded-xl shadow-sm border border-gray-200 dark:border-dark-border">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2">
                <Target className="w-5 h-5 text-blue-600" />
                <span className="text-sm font-medium text-gray-600 dark:text-dark-text-secondary">Jobs Completed</span>
              </div>
              <div className="flex items-center space-x-1">
                {getGrowthIcon(calculateGrowth(displayCurrent.jobs, displayPrevious.jobs))}
                <span className={`text-sm font-medium ${getGrowthColor(calculateGrowth(displayCurrent.jobs, displayPrevious.jobs))}`}>
                  {calculateGrowth(displayCurrent.jobs, displayPrevious.jobs)}%
                </span>
              </div>
            </div>
            <div className="space-y-2">
              <p className="text-2xl font-bold text-gray-900 dark:text-dark-text-primary">{displayCurrent.jobs}</p>
              <p className="text-xs text-gray-500 dark:text-dark-text-muted">Previous: {displayPrevious.jobs}</p>
              {renderMiniChart(monthlyData.map(d => d.jobs), 'bg-blue-200 dark:bg-blue-700')}
            </div>
          </div>

          <div className="bg-white dark:bg-dark-card p-6 rounded-xl shadow-sm border border-gray-200 dark:border-dark-border">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2">
                <Star className="w-5 h-5 text-yellow-600" />
                <span className="text-sm font-medium text-gray-600 dark:text-dark-text-secondary">Average Rating</span>
              </div>
              <div className="flex items-center space-x-1">
                {getGrowthIcon(calculateGrowth(displayCurrent.rating, displayPrevious.rating))}
                <span className={`text-sm font-medium ${getGrowthColor(calculateGrowth(displayCurrent.rating, displayPrevious.rating))}`}>
                  {calculateGrowth(displayCurrent.rating, displayPrevious.rating)}%
                </span>
              </div>
            </div>
            <div className="space-y-2">
              <p className="text-2xl font-bold text-gray-900 dark:text-dark-text-primary">{displayCurrent.rating.toFixed(1)}</p>
              <p className="text-xs text-gray-500 dark:text-dark-text-muted">Previous: {displayPrevious.rating.toFixed(1)}</p>
              <div className="flex items-center space-x-1">
                {Array.from({ length: 5 }, (_, i) => (
                  <Star
                    key={i}
                    className={`w-3 h-3 ${i < Math.floor(displayCurrent.rating) ? 'text-yellow-400 fill-current' : 'text-gray-300 dark:text-gray-600'}`}
                  />
                ))}
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-dark-card p-6 rounded-xl shadow-sm border border-gray-200 dark:border-dark-border">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2">
                <Clock className="w-5 h-5 text-purple-600" />
                <span className="text-sm font-medium text-gray-600 dark:text-dark-text-secondary">Hours Worked</span>
              </div>
              <div className="flex items-center space-x-1">
                {getGrowthIcon(calculateGrowth(displayCurrent.hours, displayPrevious.hours))}
                <span className={`text-sm font-medium ${getGrowthColor(calculateGrowth(displayCurrent.hours, displayPrevious.hours))}`}>
                  {calculateGrowth(displayCurrent.hours, displayPrevious.hours)}%
                </span>
              </div>
            </div>
            <div className="space-y-2">
              <p className="text-2xl font-bold text-gray-900 dark:text-dark-text-primary">{displayCurrent.hours}</p>
              <p className="text-xs text-gray-500 dark:text-dark-text-muted">Previous: {displayPrevious.hours}</p>
              {renderMiniChart(monthlyData.map(d => d.hours), 'bg-purple-200 dark:bg-purple-700')}
            </div>
          </div>
        </div>

        {/* Detailed Metrics */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-1 md:gap-6 mb-8">
          <div className="bg-white dark:bg-dark-card rounded-xl shadow-sm border border-gray-200 dark:border-dark-border p-2 md:p-6">
            <h3 className="text-sm md:text-lg font-semibold text-gray-900 dark:text-dark-text-primary mb-2 md:mb-4">Service Performance</h3>
            <div className="grid grid-cols-2 gap-1 md:gap-4 items-start">
              {servicePerformance.map((service, index) => (
                <div key={index} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-2 md:p-3 hover:shadow-md transition-shadow h-full flex flex-col">
                  <h4 className="font-medium text-gray-900 dark:text-dark-text-primary text-xs md:text-sm mb-1 truncate">{service.service}</h4>
                  <div className="space-y-1 text-xs md:text-sm flex-1">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600 dark:text-dark-text-secondary">Jobs:</span>
                      <span className="font-medium">{service.jobs}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600 dark:text-dark-text-secondary">Revenue:</span>
                      <span className="font-medium text-green-600">${(service.revenue / 1000).toFixed(1)}k</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600 dark:text-dark-text-secondary">Rating:</span>
                      <div className="flex items-center space-x-1">
                        <Star className="w-3 h-3 text-yellow-400" />
                        <span className="font-medium">{service.averageRating.toFixed(1)}</span>
                      </div>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600 dark:text-dark-text-secondary">Growth:</span>
                      <div className="flex items-center space-x-1">
                        {getGrowthIcon(service.growth.toString())}
                        <span className={`font-medium ${getGrowthColor(service.growth.toString())}`}>
                          {service.growth > 0 ? '+' : ''}{service.growth}%
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white dark:bg-dark-card rounded-xl shadow-sm border border-gray-200 dark:border-dark-border p-2 md:p-6">
            <h3 className="text-sm md:text-lg font-semibold text-gray-900 dark:text-dark-text-primary mb-2 md:mb-4">Client Metrics</h3>
            <div className="grid grid-cols-2 gap-1 md:gap-4 items-start">
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-2 md:p-3 hover:shadow-md transition-shadow h-full flex flex-col">
                <h4 className="font-medium text-gray-900 dark:text-dark-text-primary text-xs md:text-sm mb-1">Repeat Clients</h4>
                <div className="text-center">
                  <p className="text-lg md:text-xl font-bold text-green-600">{displayCurrent.repeatClients}</p>
                  <p className="text-xs text-gray-500 dark:text-dark-text-muted">+{displayCurrent.repeatClients - displayPrevious.repeatClients} from last period</p>
                </div>
              </div>
              
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-2 md:p-3 hover:shadow-md transition-shadow h-full flex flex-col">
                <h4 className="font-medium text-gray-900 dark:text-dark-text-primary text-xs md:text-sm mb-1">New Clients</h4>
                <div className="text-center">
                  <p className="text-lg md:text-xl font-bold text-blue-600">{displayCurrent.newClients}</p>
                  <p className="text-xs text-gray-500 dark:text-dark-text-muted">+{displayCurrent.newClients - displayPrevious.newClients} from last period</p>
                </div>
              </div>
              
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-2 md:p-3 hover:shadow-md transition-shadow h-full flex flex-col">
                <h4 className="font-medium text-gray-900 dark:text-dark-text-primary text-xs md:text-sm mb-1">Avg Job Value</h4>
                <div className="text-center">
                  <p className="text-lg md:text-xl font-bold text-purple-600">${displayCurrent.averageJobValue}</p>
                  <p className="text-xs text-gray-500 dark:text-dark-text-muted">+${displayCurrent.averageJobValue - displayPrevious.averageJobValue} from last period</p>
                </div>
              </div>
              
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-2 md:p-3 hover:shadow-md transition-shadow h-full flex flex-col">
                <h4 className="font-medium text-gray-900 dark:text-dark-text-primary text-xs md:text-sm mb-1">Completion Rate</h4>
                <div className="text-center">
                  <p className="text-lg md:text-xl font-bold text-orange-600">{displayCurrent.completionRate}%</p>
                  <p className="text-xs text-gray-500 dark:text-dark-text-muted">+{(displayCurrent.completionRate - displayPrevious.completionRate).toFixed(1)}% from last period</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Monthly Trend Chart */}
        <div className="bg-white dark:bg-dark-card rounded-xl shadow-sm border border-gray-200 dark:border-dark-border p-2 md:p-6">
          <h3 className="text-sm md:text-lg font-semibold text-gray-900 dark:text-dark-text-primary mb-2 md:mb-4">6-Month Performance Trend</h3>
          <div className="grid grid-cols-2 gap-1 md:gap-6 md:grid-cols-4">
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-2 md:p-3 hover:shadow-md transition-shadow">
              <h4 className="text-xs md:text-sm font-medium text-gray-600 dark:text-dark-text-secondary mb-1 md:mb-3">Revenue Trend</h4>
              <div className="space-y-1 md:space-y-2">
                {monthlyData.slice(0, 3).map((data, index) => (
                  <div key={index} className="flex justify-between items-center">
                    <span className="text-xs text-gray-600 dark:text-dark-text-secondary w-8">{data.month.slice(0, 3)}</span>
                    <div className="flex-1 mx-1 bg-gray-200 dark:bg-gray-600 rounded-full h-1 md:h-2">
                      <div 
                        className="bg-green-500 h-1 md:h-2 rounded-full"
                        style={{ width: `${(data.earnings / Math.max(...monthlyData.map(d => d.earnings))) * 100}%` }}
                      />
                    </div>
                    <span className="text-xs font-medium text-gray-900 dark:text-dark-text-primary w-8 text-right">
                      ${(data.earnings / 1000).toFixed(1)}k
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-2 md:p-3 hover:shadow-md transition-shadow">
              <h4 className="text-xs md:text-sm font-medium text-gray-600 dark:text-dark-text-secondary mb-1 md:mb-3">Jobs Trend</h4>
              <div className="space-y-1 md:space-y-2">
                {monthlyData.slice(0, 3).map((data, index) => (
                  <div key={index} className="flex justify-between items-center">
                    <span className="text-xs text-gray-600 dark:text-dark-text-secondary w-8">{data.month.slice(0, 3)}</span>
                    <div className="flex-1 mx-1 bg-gray-200 dark:bg-gray-600 rounded-full h-1 md:h-2">
                      <div 
                        className="bg-blue-500 h-1 md:h-2 rounded-full"
                        style={{ width: `${(data.jobs / Math.max(...monthlyData.map(d => d.jobs))) * 100}%` }}
                      />
                    </div>
                    <span className="text-xs font-medium text-gray-900 dark:text-dark-text-primary w-8 text-right">
                      {data.jobs}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-2 md:p-3 hover:shadow-md transition-shadow">
              <h4 className="text-xs md:text-sm font-medium text-gray-600 dark:text-dark-text-secondary mb-1 md:mb-3">Hours Trend</h4>
              <div className="space-y-1 md:space-y-2">
                {monthlyData.slice(0, 3).map((data, index) => (
                  <div key={index} className="flex justify-between items-center">
                    <span className="text-xs text-gray-600 dark:text-dark-text-secondary w-8">{data.month.slice(0, 3)}</span>
                    <div className="flex-1 mx-1 bg-gray-200 dark:bg-gray-600 rounded-full h-1 md:h-2">
                      <div 
                        className="bg-purple-500 h-1 md:h-2 rounded-full"
                        style={{ width: `${(data.hours / Math.max(...monthlyData.map(d => d.hours))) * 100}%` }}
                      />
                    </div>
                    <span className="text-xs font-medium text-gray-900 dark:text-dark-text-primary w-8 text-right">
                      {data.hours}h
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-2 md:p-3 hover:shadow-md transition-shadow">
              <h4 className="text-xs md:text-sm font-medium text-gray-600 dark:text-dark-text-secondary mb-1 md:mb-3">Rating Trend</h4>
              <div className="space-y-1 md:space-y-2">
                {monthlyData.slice(0, 3).map((data, index) => (
                  <div key={index} className="flex justify-between items-center">
                    <span className="text-xs text-gray-600 dark:text-dark-text-secondary w-8">{data.month.slice(0, 3)}</span>
                    <div className="flex-1 mx-1 bg-gray-200 dark:bg-gray-600 rounded-full h-1 md:h-2">
                      <div 
                        className="bg-yellow-500 h-1 md:h-2 rounded-full"
                        style={{ width: `${(data.rating / 5) * 100}%` }}
                      />
                    </div>
                    <span className="text-xs font-medium text-gray-900 dark:text-dark-text-primary w-8 text-right">
                      {data.rating.toFixed(1)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}
